import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSupabaseServiceKey, setSupabaseServiceKey } from '../../services/auth';
import { toast } from '../../hooks/use-toast';
import UserManagement from './UserManagement';

import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Checkbox } from '../ui/checkbox';
import { Separator } from '../ui/separator';

type TabKey = 'api' | 'users' | 'webhooks';

type ApiConfig = {
  supabase: string;
  whatsapp: string;
  facebook: string;
  instagram: string;
  tiktok: string;
  email: string;
  webhook: string;
};

export default function SettingsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabKey>('api');

  const [config, setConfig] = useState<ApiConfig>(() => {
    try {
      return {
        supabase: getSupabaseServiceKey() || '',
        whatsapp: localStorage.getItem('whatsapp_api_key') || '',
        facebook: localStorage.getItem('facebook_api_key') || '',
        instagram: localStorage.getItem('instagram_api_key') || '',
        tiktok: localStorage.getItem('tiktok_api_key') || '',
        email: localStorage.getItem('email_api_key') || '',
        webhook: localStorage.getItem('webhook_url') || '',
      };
    } catch {
      return {
        supabase: '',
        whatsapp: '',
        facebook: '',
        instagram: '',
        tiktok: '',
        email: '',
        webhook: '',
      };
    }
  });

  const [events, setEvents] = useState(() => {
    try {
      const raw = localStorage.getItem('webhook_events');
      if (raw) return JSON.parse(raw) as Record<string, boolean>;
    } catch {}
    return {
      campaign_created: true,
      campaign_started: true,
      campaign_completed: true,
      customer_created: false,
      churn_risk: false,
      target_group_updated: false,
    } as Record<string, boolean>;
  });

  const hasSupabaseKey = useMemo(() => !!config.supabase.trim(), [config.supabase]);

  const handleChange = (key: keyof ApiConfig, value: string) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    try {
      if (config.supabase.trim()) {
        setSupabaseServiceKey(config.supabase.trim());
      }
      localStorage.setItem('whatsapp_api_key', config.whatsapp);
      localStorage.setItem('facebook_api_key', config.facebook);
      localStorage.setItem('instagram_api_key', config.instagram);
      localStorage.setItem('tiktok_api_key', config.tiktok);
      localStorage.setItem('email_api_key', config.email);
      localStorage.setItem('webhook_url', config.webhook);
      localStorage.setItem('webhook_events', JSON.stringify(events));

      toast({
        title: 'Configurações salvas',
        description: 'As chaves e webhooks foram atualizados com sucesso.',
      });
    } catch (e: any) {
      toast({
        title: 'Erro ao salvar',
        description: e?.message || 'Não foi possível salvar as configurações.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="min-h-[calc(100vh-60px)] w-full">
      <div className="mx-auto w-full max-w-6xl space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold leading-tight">Configurações</h1>
            <p className="text-sm text-muted-foreground">
              Gerencie chaves de API, usuários do time e webhooks.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate(-1)}>
              Voltar
            </Button>
            <Button onClick={handleSave}>Salvar</Button>
          </div>
        </div>

        {!hasSupabaseKey && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle>Atenção: Supabase Service Key não configurada</CardTitle>
              <CardDescription>
                Algumas telas (ex.: Target Groups, Attributes, Customer 360) precisam da Service Key para
                acessar os dados.
              </CardDescription>
            </CardHeader>
          </Card>
        )}

        <Tabs value={tab} onValueChange={(v) => setTab(v as TabKey)}>
          <TabsList className="w-full sm:w-auto">
            <TabsTrigger value="api">API Keys</TabsTrigger>
            <TabsTrigger value="users">Usuários</TabsTrigger>
            <TabsTrigger value="webhooks">Webhooks</TabsTrigger>
          </TabsList>

          <TabsContent value="api" className="mt-4 space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Supabase</CardTitle>
                <CardDescription>Necessário para acessar dados e Edge Functions com permissão.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <Label htmlFor="supabase_service_key">Service Key</Label>
                <Input
                  id="supabase_service_key"
                  type="password"
                  value={config.supabase}
                  onChange={(e) => handleChange('supabase', e.target.value)}
                  placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
                />
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>WhatsApp Business</CardTitle>
                  <CardDescription>Token/Access Token para envio e leitura de mensagens.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Label htmlFor="whatsapp_key">API Key / Access Token</Label>
                  <Input
                    id="whatsapp_key"
                    type="password"
                    value={config.whatsapp}
                    onChange={(e) => handleChange('whatsapp', e.target.value)}
                    placeholder="EAAxxxxxxxxxxxxx"
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Instagram</CardTitle>
                  <CardDescription>Token para Instagram Graph API.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Label htmlFor="instagram_key">Access Token</Label>
                  <Input
                    id="instagram_key"
                    type="password"
                    value={config.instagram}
                    onChange={(e) => handleChange('instagram', e.target.value)}
                    placeholder="IGQVJxxxxxxxxxxxxx"
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Facebook</CardTitle>
                  <CardDescription>Token para Facebook Graph API.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Label htmlFor="facebook_key">Access Token</Label>
                  <Input
                    id="facebook_key"
                    type="password"
                    value={config.facebook}
                    onChange={(e) => handleChange('facebook', e.target.value)}
                    placeholder="EAAxxxxxxxxxxxxx"
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>TikTok</CardTitle>
                  <CardDescription>Token para TikTok Marketing API.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Label htmlFor="tiktok_key">Access Token</Label>
                  <Input
                    id="tiktok_key"
                    type="password"
                    value={config.tiktok}
                    onChange={(e) => handleChange('tiktok', e.target.value)}
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  />
                </CardContent>
              </Card>

              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>Email</CardTitle>
                  <CardDescription>API Key (SendGrid/Mailgun/etc) ou credenciais SMTP.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Label htmlFor="email_key">SMTP / API Key</Label>
                  <Input
                    id="email_key"
                    type="password"
                    value={config.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="SG.xxxxxxxxxxxxx ou SMTP config"
                  />
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="users" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Usuários</CardTitle>
                <CardDescription>Criação e gerenciamento do time.</CardDescription>
              </CardHeader>
              <CardContent>
                <UserManagement />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="webhooks" className="mt-4 space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Webhook URL</CardTitle>
                <CardDescription>Receba eventos em um endpoint externo.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <Label htmlFor="webhook_url">URL</Label>
                <Input
                  id="webhook_url"
                  type="url"
                  value={config.webhook}
                  onChange={(e) => handleChange('webhook', e.target.value)}
                  placeholder="https://seu-servidor.com/webhook"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Eventos</CardTitle>
                <CardDescription>Marque o que deseja disparar via webhook.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={!!events.campaign_created}
                    onCheckedChange={(v) =>
                      setEvents((p) => ({ ...p, campaign_created: v === true }))
                    }
                    id="evt_campaign_created"
                  />
                  <Label htmlFor="evt_campaign_created">Nova campanha criada</Label>
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={!!events.campaign_started}
                    onCheckedChange={(v) =>
                      setEvents((p) => ({ ...p, campaign_started: v === true }))
                    }
                    id="evt_campaign_started"
                  />
                  <Label htmlFor="evt_campaign_started">Campanha iniciada</Label>
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={!!events.campaign_completed}
                    onCheckedChange={(v) =>
                      setEvents((p) => ({ ...p, campaign_completed: v === true }))
                    }
                    id="evt_campaign_completed"
                  />
                  <Label htmlFor="evt_campaign_completed">Campanha concluída</Label>
                </div>

                <Separator />

                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={!!events.customer_created}
                    onCheckedChange={(v) =>
                      setEvents((p) => ({ ...p, customer_created: v === true }))
                    }
                    id="evt_customer_created"
                  />
                  <Label htmlFor="evt_customer_created">Novo cliente cadastrado</Label>
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={!!events.churn_risk}
                    onCheckedChange={(v) =>
                      setEvents((p) => ({ ...p, churn_risk: v === true }))
                    }
                    id="evt_churn_risk"
                  />
                  <Label htmlFor="evt_churn_risk">Cliente com risco de churn</Label>
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={!!events.target_group_updated}
                    onCheckedChange={(v) =>
                      setEvents((p) => ({ ...p, target_group_updated: v === true }))
                    }
                    id="evt_target_group_updated"
                  />
                  <Label htmlFor="evt_target_group_updated">Target group atualizado</Label>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}


